import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Star, Clock, Phone, Globe, Bike, Map, Footprints, Car, Zap, Plug } from 'lucide-react';
import './POIsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function POIsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(searchParams.get('activity_type') || '');
  const [bikeFriendlyOnly, setBikeFriendlyOnly] = useState(false);
  const [groupedPOIs, setGroupedPOIs] = useState({});

  useEffect(() => {
    fetchPOIs();
  }, [selectedCategory, selectedActivity, bikeFriendlyOnly]);

  const fetchPOIs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }
      if (selectedActivity) {
        params.append('activity_type', selectedActivity);
      }
      const queryString = params.toString();
      const response = await axios.get(`${API}/pois${queryString ? `?${queryString}` : ''}`);
      let poisData = response.data;
      
      // Filter bike-friendly if checked
      if (bikeFriendlyOnly) {
        poisData = poisData.filter(poi => poi.is_bike_friendly === true);
      }
      
      setPois(poisData);
      
      // Group POIs by category
      const grouped = poisData.reduce((acc, poi) => {
        const category = poi.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(poi);
        return acc;
      }, {});
      
      setGroupedPOIs(grouped);
    } catch (error) {
      console.error('Error fetching POIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: '', name: t('filters.all'), icon: '🗺️' },
    { id: 'culture', name: t('categories.culture'), icon: '🏰' },
    { id: 'nature', name: t('categories.nature'), icon: '🏔️' },
    { id: 'food', name: t('categories.food'), icon: '🥃' },
    { id: 'whisky', name: t('categories.whisky'), icon: '🥃' },
    { id: 'tour_operators', name: t('categories.tour_operators'), icon: '🚴' },
    { id: 'infrastructure', name: t('categories.infrastructure'), icon: '🔧' },
    { id: 'accommodation', name: t('categories.accommodation'), icon: '⛺' }
  ];

  const getCategoryIcon = (category) => {
    const icons = {
      culture: '🏰',
      nature: '🏔️',
      food: '🥃',
      whisky: '🥃',
      tour_operators: '🚴',
      infrastructure: '🔧',
      accommodation: '⛺'
    };
    return icons[category] || '📍';
  };

  if (loading) {
    return (
      <div className="loading-container" data-testid="pois-loading">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="pois-page" data-testid="pois-page">
      <header className="page-header">
        <div>
          <h1 className="page-title" data-testid="pois-title">Points of Interest</h1>
          <p className="page-subtitle">{t('pois.subtitle')}</p>
        </div>
      </header>

      <div className="category-filter" data-testid="category-filter">
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
            data-testid={`category-btn-${cat.id || 'all'}`}
          >
            <span className="category-icon">{cat.icon}</span>
            <span>{cat.name}</span>
          </button>
        ))}
      </div>

      <div className="activity-filter" data-testid="activity-filter">
        <div className="filter-row">
          <div className="filter-group">
            <label>{t('filters.activity_type')}</label>
            <select
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
              data-testid="filter-activity"
              className="activity-filter-select"
            >
              <option value="">{t('filters.all')}</option>
              <option value="cycling">🚴 {t('activities.cycling')}</option>
              <option value="hiking">🥾 {t('activities.hiking')}</option>
              <option value="roadtrip">🚗 {t('activities.roadtrip')}</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={bikeFriendlyOnly}
                onChange={(e) => setBikeFriendlyOnly(e.target.checked)}
                className="checkbox"
              />
              <span>🚴 Fahrradfreundlich</span>
            </label>
          </div>
        </div>
      </div>

      <div className="pois-count" data-testid="pois-count">
        {pois.length} {pois.length === 1 ? 'Ort' : 'Orte'} gefunden
      </div>

      {pois.length === 0 ? (
        <div className="no-pois" data-testid="no-pois">
          <MapPin size={64} color="var(--text-secondary)" />
          <p>{t('no_data')}</p>
        </div>
      ) : selectedCategory ? (
        // Single category view (existing grid)
        <div className="pois-grid">
          {pois.map((poi) => (
            <div key={poi.id} className="poi-card" data-testid={`poi-card-${poi.id}`}>
              <div className="poi-header">
                <span className="poi-category-icon">{getCategoryIcon(poi.category)}</span>
                <div className="poi-category-name">
                  {t(`categories.${poi.category}`)}
                </div>
                {poi.is_bike_friendly && (
                  <span className="poi-bike-friendly" data-testid="bike-friendly-badge">
                    <Bike size={16} />
                  </span>
                )}
              </div>

              {poi.activity_types && poi.activity_types.length > 0 && (
                <div className="poi-activities">
                  {poi.activity_types.map((activity) => (
                    <span key={activity} className={`poi-activity activity-${activity}`}>
                      {activity === 'cycling' && <Bike size={14} />}
                      {activity === 'hiking' && <Footprints size={14} />}
                      {activity === 'roadtrip' && <Car size={14} />}
                      {t(`activities.${activity}`)}
                    </span>
                  ))}
                </div>
              )}

              <h3 className="poi-name" data-testid="poi-name">{poi.name}</h3>
              <p className="poi-description">{poi.description}</p>

              {poi.rating > 0 && (
                <div className="poi-rating">
                  <Star size={16} fill="#fbbf24" color="#fbbf24" />
                  <span>{poi.rating.toFixed(1)}</span>
                </div>
              )}

              <div className="poi-details">
                {poi.address && (
                  <div className="poi-detail">
                    <MapPin size={16} />
                    <span>{poi.address}</span>
                  </div>
                )}
                {poi.opening_hours && (
                  <div className="poi-detail">
                    <Clock size={16} />
                    <span>{poi.opening_hours}</span>
                  </div>
                )}
                {poi.phone && (
                  <div className="poi-detail">
                    <Phone size={16} />
                    <a href={`tel:${poi.phone}`}>{poi.phone}</a>
                  </div>
                )}
                {poi.charging_plugs && poi.charging_plugs.length > 0 && (
                  <div className="poi-detail charging-info">
                    <Zap size={16} color="#10b981" />
                    <div className="charging-plugs">
                      {poi.charging_plugs.map((plug, idx) => (
                        <span key={idx} className={`plug-type plug-${plug.toLowerCase()}`} title={plug}>
                          {plug}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {poi.tags.length > 0 && (
                <div className="poi-tags">
                  {poi.tags.map((tag, idx) => (
                    <span key={idx} className="poi-tag">#{tag}</span>
                  ))}
                </div>
              )}

              <div className="poi-actions">
                {poi.website && (
                  <a 
                    href={poi.website.startsWith('http') ? poi.website : `https://${poi.website}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="poi-website-btn"
                  >
                    <Globe size={16} />
                    Website besuchen
                  </a>
                )}
                <Link 
                  to={`/map?poiId=${poi.id}`}
                  className="poi-map-link"
                >
                  <Map size={18} />
                  {t('pois.show_on_map')}
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Grouped by category view
        <div className="pois-by-category">
          {Object.entries(groupedPOIs).map(([category, categoryPOIs]) => (
            <section key={category} className="category-section" data-testid={`category-section-${category}`}>
              <div className="category-section-header">
                <span className="category-section-icon">{getCategoryIcon(category)}</span>
                <h2 className="category-section-title">{t(`categories.${category}`)}</h2>
                <span className="category-section-count">({categoryPOIs.length})</span>
              </div>

              <div className="pois-grid">
                {categoryPOIs.map((poi) => (
                  <div key={poi.id} className="poi-card" data-testid={`poi-card-${poi.id}`}>
                    <div className="poi-header">
                      <span className="poi-category-icon">{getCategoryIcon(poi.category)}</span>
                      <div className="poi-category-name">
                        {poi.subcategory}
                      </div>
                      {poi.is_bike_friendly && (
                        <span className="poi-bike-friendly" data-testid="bike-friendly-badge">
                          <Bike size={16} />
                        </span>
                      )}
                    </div>

                    {poi.activity_types && poi.activity_types.length > 0 && (
                      <div className="poi-activities">
                        {poi.activity_types.map((activity) => (
                          <span key={activity} className={`poi-activity activity-${activity}`}>
                            {activity === 'cycling' && <Bike size={14} />}
                            {activity === 'hiking' && <Footprints size={14} />}
                            {activity === 'roadtrip' && <Car size={14} />}
                            {t(`activities.${activity}`)}
                          </span>
                        ))}
                      </div>
                    )}

                    <h3 className="poi-name" data-testid="poi-name">{poi.name}</h3>
                    <p className="poi-description">{poi.description}</p>

                    {poi.rating > 0 && (
                      <div className="poi-rating">
                        <Star size={16} fill="#fbbf24" color="#fbbf24" />
                        <span>{poi.rating.toFixed(1)}</span>
                      </div>
                    )}

                    <div className="poi-details">
                      {poi.address && (
                        <div className="poi-detail">
                          <MapPin size={16} />
                          <span>{poi.address}</span>
                        </div>
                      )}
                      {poi.opening_hours && (
                        <div className="poi-detail">
                          <Clock size={16} />
                          <span>{poi.opening_hours}</span>
                        </div>
                      )}
                      {poi.phone && (
                        <div className="poi-detail">
                          <Phone size={16} />
                          <a href={`tel:${poi.phone}`}>{poi.phone}</a>
                        </div>
                      )}
                    </div>

                    {poi.tags.length > 0 && (
                      <div className="poi-tags">
                        {poi.tags.map((tag, idx) => (
                          <span key={idx} className="poi-tag">#{tag}</span>
                        ))}
                      </div>
                    )}

                    <div className="poi-actions">
                      {poi.website && (
                        <a 
                          href={poi.website.startsWith('http') ? poi.website : `https://${poi.website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="poi-website-btn"
                        >
                          <Globe size={16} />
                          Website besuchen
                        </a>
                      )}
                      <Link 
                        to={`/map?poiId=${poi.id}`}
                        className="poi-map-link"
                      >
                        <Map size={18} />
                        {t('pois.show_on_map')}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
